// "How this answer was made", on the backoffice's thread page (2026-09-27;
// `document-ai/0022`). The owner: "In the backoffice we need to be able to
// see the debug view of the answer in its entirety."
//
// Under each answer, open by default, because the backoffice is for support:
// - **the answer as stored, whole:** its text, the review's fields (headline,
//   supporting lines, level and note), the topic, how it ended, its counts,
//   the unfinished and coverage records, and every source in full. The whole
//   record is a click away as JSON.
// - **every step and every model call, in one list, in order.** A call with
//   no step row, such as the naming of the question's language, has its
//   place here too. A step renders as the steps block renders it
//   (`answer-steps.tsx`). A call is a line that opens to its system prompt,
//   its prompt, its thinking and its output, read from the run route when it
//   is opened. "Open all" opens every call.
//
// An answer with no stored record shows the list alone: one still running,
// and one that failed or was refused with no text, which is never stored
// (`ask-runner.service.ts`). The failed one is what support most needs to
// read, with the failed call and the provider's message.
//
// A read that failed says so, and "nothing was recorded" is said only when
// both reads came back and both were empty.
//
// A call is placed by the dashboard's rule (`interleave` in its
// `features/debug/debug-panels.tsx`): after the last step that had started
// when the call was recorded, and before every step when it was recorded
// first. The rule is rewritten here rather than imported: the two apps share
// contracts, not components (`architecture/0022`).

import { useState, type ReactNode } from "react";
import { ChevronRight, Cpu } from "lucide-react";
import { Link } from "react-router";
import type { AdminAnswerRun, AdminAnswerRuns } from "@gb/admin-contracts";
import { formatCoverage, formatUnfinished, type ChatMessage, type ChatSource } from "@gb/contracts";
import { cn } from "@/lib/utils";
import { useRun } from "../runs/runs-api";
import { Body, StepRow, took, type ShownStep } from "./answer-steps";

/** The reader's locale: numbers and dates follow the reader, the words stay British English. */
const LOCALE: string | undefined = undefined;

function num(n: number): string {
  return new Intl.NumberFormat(LOCALE).format(n);
}

export type MadeItem = { kind: "step"; step: ShownStep } | { kind: "call"; run: AdminAnswerRun };

/**
 * The steps in order of `seq`, each call after the last step that had
 * started when the call was recorded; calls recorded before any step first.
 * Every step and every call is in the list once: nothing is dropped when the
 * clocks disagree, only placed a row early or late.
 */
export function interleave(steps: readonly ShownStep[], runs: readonly AdminAnswerRun[]): MadeItem[] {
  const ordered = [...steps].sort((a, b) => a.seq - b.seq);
  const after = new Map<number, AdminAnswerRun[]>();
  const first: AdminAnswerRun[] = [];
  for (const run of [...runs].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))) {
    const at = Date.parse(run.createdAt);
    let index = -1;
    ordered.forEach((step, each) => {
      if (Date.parse(step.startedAt) <= at) index = each;
    });
    if (index < 0) first.push(run);
    else after.set(index, [...(after.get(index) ?? []), run]);
  }
  return [
    ...first.map((run): MadeItem => ({ kind: "call", run })),
    ...ordered.flatMap((step, index): MadeItem[] => [
      { kind: "step", step },
      ...(after.get(index) ?? []).map((run): MadeItem => ({ kind: "call", run })),
    ]),
  ];
}

/** A read, as its query gives it. */
export type Read<T> = { data: T | undefined; isPending: boolean; error: Error | null };

/** The tenant, for each call's bodies and its page (`runs/:tenantSlug/:runId`). */
export type TenantRead = { slug: string | null; error: Error | null };

export function AnswerMade({
  answer,
  running = false,
  steps,
  runs,
  tenant,
}: {
  /** The stored answer. Null while it runs, and when it failed or was refused with no text: it was never stored. */
  answer: ChatMessage | null;
  /** Whether the answer may still be running, so an empty list means "nothing yet". */
  running?: boolean;
  /** The thread's steps. */
  steps: Read<readonly ShownStep[]>;
  /** The thread's calls. */
  runs: Read<AdminAnswerRuns>;
  tenant: TenantRead;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section aria-label="How this answer was made" className="mt-4 rounded-lg border border-border bg-canvas">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left"
      >
        <ChevronRight size={14} className={cn("shrink-0 text-muted transition-transform", open && "rotate-90")} aria-hidden />
        <span className="min-w-0 flex-1 text-[0.8rem] font-semibold text-ink">How this answer was made</span>
        {steps.data && runs.data && (
          <span className="shrink-0 font-mono text-[0.72rem] text-faint">
            {num(steps.data.length)} {steps.data.length === 1 ? "step" : "steps"} · {runs.data.more ? "more than " : ""}
            {num(runs.data.runs.length)} {runs.data.runs.length === 1 && !runs.data.more ? "model call" : "model calls"}
          </span>
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-4 border-t border-border-inner px-3 py-3">
          {answer && <StoredAnswer message={answer} />}
          <Timeline running={running} steps={steps} runs={runs} tenant={tenant} />
        </div>
      )}
    </section>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <p className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">{children}</p>;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-ink">{children}</dd>
    </div>
  );
}

// ------------------------------------------------------------------
// The answer as stored
// ------------------------------------------------------------------

function StoredAnswer({ message }: { message: ChatMessage }) {
  const [json, setJson] = useState(false);
  const trail = message.trail ?? null;
  // An answer from before the agent, or a scripted one, has no trail: its
  // records were never written, which is not the same as "none".
  const notRecorded = "Not recorded";
  return (
    <div className="flex flex-col gap-2 text-[0.76rem]">
      <Heading>The answer as stored</Heading>
      <dl className="flex flex-col gap-0.5">
        <Fact label="Answer id">
          <span className="font-mono">{message.id}</span>
        </Fact>
        <Fact label="Stored">{new Date(message.createdAt).toLocaleString(LOCALE)}</Fact>
        <Fact label="Topic">{message.topic ?? "None"}</Fact>
        <Fact label="Confidence">{message.confidence ?? "Not assessed"}</Fact>
        <Fact label="Its note">{message.confidenceNote ?? "None"}</Fact>
        <Fact label="Headline">{message.headline ?? "None"}</Fact>
        <Fact label="Supporting">
          {message.supporting.length === 0 ? (
            "None"
          ) : (
            <ul className="ml-3 list-disc">
              {message.supporting.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          )}
        </Fact>
        <Fact label="Ended by">{trail ? trail.endedBy : notRecorded}</Fact>
        <Fact label="Steps">
          {trail
            ? `${num(trail.steps)} ${trail.steps === 1 ? "tool call" : "tool calls"}, ${num(trail.turns)} ${trail.turns === 1 ? "model turn" : "model turns"}, ${took(trail.elapsedMs)}`
            : notRecorded}
        </Fact>
        <Fact label="Unfinished">
          {message.unfinished ? formatUnfinished(message.unfinished, LOCALE) : trail ? "No" : notRecorded}
        </Fact>
        <Fact label="Coverage">{message.coverage ? formatCoverage(message.coverage, LOCALE) : notRecorded}</Fact>
        <Fact label="Marked wrong">{message.markedWrong ? "Yes" : "No"}</Fact>
      </dl>
      <Body label="The text, as stored" text={message.content} />
      <StoredSources sources={message.sources} />
      <div>
        <button
          type="button"
          aria-expanded={json}
          onClick={() => setJson(!json)}
          className="text-[0.72rem] text-brand hover:underline"
        >
          {json ? "Hide the record as JSON" : "Show the record as JSON"}
        </button>
        {json && <Body label="The record, as stored" text={JSON.stringify(message, null, 2)} />}
      </div>
    </div>
  );
}

function StoredSources({ sources }: { sources: readonly ChatSource[] }) {
  if (sources.length === 0) return <p className="text-muted">No source was cited.</p>;
  return (
    <div>
      <p className="font-semibold text-ink-slate">
        Sources, in full <span className="font-normal text-muted">({num(sources.length)})</span>
      </p>
      <ol className="mt-0.5 flex flex-col gap-1">
        {sources.map((source) => (
          <SourceRow key={source.n} source={source} />
        ))}
      </ol>
    </div>
  );
}

/** A source: its line and its snippet whole, and the source as stored, as JSON, on a click. */
function SourceRow({ source }: { source: ChatSource }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-start gap-1.5 rounded px-1 py-0.5 text-left hover:bg-white"
      >
        <ChevronRight size={13} className={cn("mt-0.5 shrink-0 text-faint transition-transform", open && "rotate-90")} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="font-mono text-faint">[{source.n}]</span> <span className="font-semibold text-ink">{source.title}</span>{" "}
          <span className="text-faint">
            — {source.citation} · {source.kind}
            {source.date ? ` · ${source.date}` : " · undated"}
          </span>
        </span>
      </button>
      <p className="ml-6 whitespace-pre-wrap text-muted">{source.snippet}</p>
      {open && (
        <pre className="mt-0.5 ml-6 max-h-[28rem] overflow-auto rounded bg-white p-2 font-mono text-[0.72rem] leading-[1.4] whitespace-pre-wrap text-ink">
          {JSON.stringify(source, null, 2)}
        </pre>
      )}
    </li>
  );
}

// ------------------------------------------------------------------
// Every step and every model call
// ------------------------------------------------------------------

function Timeline({
  running,
  steps,
  runs,
  tenant,
}: {
  running: boolean;
  steps: Read<readonly ShownStep[]>;
  runs: Read<AdminAnswerRuns>;
  tenant: TenantRead;
}) {
  const calls = runs.data?.runs ?? [];
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const allOpen = calls.length > 0 && calls.every((run) => opened.has(run.id));
  const toggle = (id: string) =>
    setOpened((now) => {
      const next = new Set(now);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const items = interleave(steps.data ?? [], calls);
  // "Nothing was recorded" is a claim about both tables: only when both reads came back.
  const bothRead = steps.data !== undefined && runs.data !== undefined && !steps.error && !runs.error;
  return (
    <div className="flex flex-col gap-1 text-[0.76rem]">
      <div className="flex items-center gap-3">
        <Heading>Every step and every model call, in order</Heading>
        {calls.length > 0 && (
          <button
            type="button"
            onClick={() => setOpened(allOpen ? new Set() : new Set(calls.map((run) => run.id)))}
            className="text-[0.72rem] text-brand hover:underline"
          >
            {allOpen ? "Close all" : "Open all"}
          </button>
        )}
      </div>
      {(steps.isPending || runs.isPending) && (
        <p role="status" className="text-muted">
          Reading the steps and the model calls…
        </p>
      )}
      {steps.error && (
        <p role="alert" className="font-semibold text-danger">
          The steps could not be read: {steps.error.message}
        </p>
      )}
      {runs.error && (
        <p role="alert" className="font-semibold text-danger">
          The model calls could not be read: {runs.error.message}
        </p>
      )}
      {runs.data?.more && (
        <p className="font-semibold text-warn-text">
          This answer has more model calls than the list holds. Only the first {num(calls.length)} are listed.
        </p>
      )}
      {items.length === 0 ? (
        bothRead && (
          <p className="text-muted">
            {running
              ? "No step and no model call yet."
              : "No step and no model call was recorded. A scripted demo answer makes none."}
          </p>
        )
      ) : (
        <ol aria-label="Steps and model calls" className="flex flex-col gap-1">
          {items.map((item) =>
            item.kind === "step" ? (
              <StepRow key={`step-${item.step.seq}`} step={item.step} tenantSlug={tenant.slug} />
            ) : (
              <CallRow
                key={`call-${item.run.id}`}
                run={item.run}
                open={opened.has(item.run.id)}
                onToggle={() => toggle(item.run.id)}
                tenant={tenant}
              />
            ),
          )}
        </ol>
      )}
    </div>
  );
}

/** "9,000 in, 300 out, 8,000 cache read, 1,000 cache write"; a dash where the provider gave no count. */
function tokenLine(run: AdminAnswerRun): string {
  const n = (value: number | null) => (value === null ? "—" : num(value));
  return `${n(run.inputTokens)} in, ${n(run.outputTokens)} out, ${n(run.cacheReadTokens)} cache read, ${n(run.cacheWriteTokens)} cache write`;
}

/** The collapsed line: touchpoint, model, turn, time, how long it took, and its tokens. */
function callLine(run: AdminAnswerRun): string {
  return [
    run.touchpoint,
    run.model,
    run.turn !== null ? `turn ${num(run.turn)}` : null,
    new Date(run.createdAt).toLocaleTimeString(LOCALE),
    `took ${took(run.latencyMs)}`,
    `tokens ${tokenLine(run)}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

function CallRow({
  run,
  open,
  onToggle,
  tenant,
}: {
  run: AdminAnswerRun;
  open: boolean;
  onToggle: () => void;
  tenant: TenantRead;
}) {
  return (
    <li className="text-[0.8rem]">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-left hover:bg-white"
      >
        <span className="w-5 shrink-0" aria-hidden />
        <Cpu size={14} className="mt-0.5 shrink-0 text-brand" aria-hidden />
        <span className="min-w-0 flex-1 font-mono text-[0.72rem] break-words text-ink-slate">{callLine(run)}</span>
        <span className={cn("shrink-0 font-mono text-[0.68rem]", run.status === "ok" ? "text-faint" : "text-danger")}>
          {run.status}
        </span>
        <ChevronRight size={13} className={cn("mt-0.5 shrink-0 text-faint transition-transform", open && "rotate-90")} aria-hidden />
      </button>
      {/* A failure reads without opening the call. */}
      {run.errorClass && !open && <p className="ml-14 font-mono text-[0.72rem] text-danger">{run.errorClass}</p>}
      {open && <CallBody run={run} tenant={tenant} />}
    </li>
  );
}

/** A call's bodies, read when it is opened. They are read by the tenant's slug, so not before the tenant is read. */
function CallBody({ run, tenant }: { run: AdminAnswerRun; tenant: TenantRead }) {
  const tenantSlug = tenant.slug;
  const body = useRun(tenantSlug ?? "", run.id, { enabled: tenantSlug !== null });
  return (
    <div className="mt-1 mb-2 ml-8 flex flex-col gap-2 rounded border border-border-inner bg-white p-2 text-[0.76rem]">
      <dl className="flex flex-col gap-0.5">
        <Fact label="Call">
          {tenantSlug ? (
            <Link to={`/runs/${tenantSlug}/${run.id}`} className="font-mono text-brand hover:underline">
              {run.id}
            </Link>
          ) : (
            <span className="font-mono">{run.id}</span>
          )}
        </Fact>
        <Fact label="Pictures sent">{run.imageCount === null ? "Not counted" : num(run.imageCount)}</Fact>
        {run.errorClass && <Fact label="Failed">{run.errorClass}</Fact>}
        {run.errorMessage && <Fact label="Provider said">{run.errorMessage}</Fact>}
      </dl>
      {/* A read that is never enabled would say "Reading" for ever. */}
      {tenantSlug === null && tenant.error ? (
        <p role="alert" className="font-semibold text-danger">
          The tenant could not be read, so the call cannot be opened: {tenant.error.message}
        </p>
      ) : body.isError ? (
        <p role="alert" className="font-semibold text-danger">
          The call could not be read: {body.error.message}
        </p>
      ) : !body.data ? (
        <p role="status" className="text-muted">
          Reading the call…
        </p>
      ) : (
        <>
          <Text label="System prompt, as sent" text={body.data.systemText} missing="Not recorded. Before 25 September 2026 only the source selector’s was kept." />
          <Text label="Prompt, as sent" text={body.data.promptText} missing="Not recorded." />
          <Text label="Thinking" text={body.data.thinking} missing="None returned." />
          <Text
            label="Output"
            text={body.data.output === null || body.data.output === undefined ? null : JSON.stringify(body.data.output, null, 2)}
            missing="None recorded."
          />
        </>
      )}
    </div>
  );
}

function Text({ label, text, missing }: { label: string; text: string | null; missing: string }) {
  if (text === null) {
    return (
      <p>
        <span className="font-semibold text-ink-slate">{label}:</span> <span className="text-muted">{missing}</span>
      </p>
    );
  }
  return <Body label={label} text={text} />;
}
